import { Component, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { Table } from "../s25-table/Table";
import { Bind } from "../../decorators/bind.decorator";
import { BoardService } from "../s25-swarm-schedule/s25.board.service";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { GenericTableMultiselectSearchCriteriaComponent } from "../s25-table/generics/generic.table.multiselect.search.criteria.component";
import { S25Util } from "../../util/s25-util";
import { S25ItemI } from "../../pojo/S25ItemI";
import { CacheRepository } from "../../decorators/cache.decorator";
import { SwarmSchedule } from "../s25-swarm-schedule/SwarmSchedule";
import { GenericTableListComponent } from "../s25-table/generics/generic.table.list.component";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { UserprefService } from "../../services/userpref.service";
import { ContactService } from "../../services/contact.service";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { LynxService } from "../../services/lynx.service";

@Component({
    selector: "s25-ng-meeting-pattern-grid-list",
    template: `
        @if (isInit) {
            <s25-ng-table
                [dataSource]="tableModel"
                [columnSortable]="true"
                [hasFilter]="true"
                [hasRefresh]="true"
                [unlimitedWidth]="true"
                [noResultsMessage]="'You have not created any Optimizer runs and none have been shared with you'"
            ></s25-ng-table>
            @if (boards?.length) {
                <p class="viewHint">Select "View" in the table above to view a board</p>
            }

            <s25-ng-modal #editModal [size]="'xs'">
                <ng-template #s25ModalBody let-board="board">
                    <s25-ng-edit-mpg [grid]="board" (done)="editModal.close()" />
                </ng-template>
            </s25-ng-modal>
        }
    `,
    styles: `
        .viewHint {
            text-align: center;
            padding: 1rem;
        }
    `,
    standalone: false,
})
export class S25MeetingPatternGridListComponent implements OnInit {
    @Output() viewBoard = new EventEmitter<SwarmSchedule.BoardI>();

    @ViewChild(S25TableComponent) table: S25TableComponent;
    @ViewChild("editModal") editModal: S25ModalComponent<void>;

    isInit = false;
    boards: SwarmSchedule.BoardI[];
    tableModel: Table.DataSource;
    dateFormat: Awaited<ReturnType<typeof UserprefService.getS25Dateformat>>;
    currentUsername: string;

    async ngOnInit() {
        const pushbackEnabled = await LynxService.getExpSecEnabled();

        const pb = { id: "pushback", header: "Editable", width: 180, cells: { component: GenericTableListComponent } };

        this.tableModel = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: [
                { id: "board", header: "Name" },
                { id: "locationSearch", header: "Location Search" },
                { id: "eventSearch", header: "Event Search" },
                { id: "lastRun", header: "Last Prepared" },
                ...(pushbackEnabled ? [pb] : []),
                {
                    id: "editors",
                    header: "Editors",
                    cells: { component: GenericTableMultiselectSearchCriteriaComponent },
                    sortable: false,
                },
                GenericTableButtonComponent.Column("View", (row) => this.viewBoard.emit(row.data.board)),
                GenericTableButtonComponent.Column("Edit", this.onEditClick),
            ],
        };
        this.isInit = true;
    }

    @Bind
    async getRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (query.forceRefresh) CacheRepository.invalidateByService("BoardService");
        const [boards, dateFormat, currentUsername] = await Promise.all([
            BoardService.getBoards(),
            UserprefService.getS25Dateformat(),
            ContactService.getCurrentUsername(),
        ]);
        this.boards = boards;
        this.dateFormat = dateFormat;
        this.currentUsername = currentUsername;

        const rows: Table.Row[] = boards.map(this.mapToRow);
        return { rows };
    }

    @Bind
    mapToRow(board: SwarmSchedule.BoardI) {
        const multiselectModel: MultiselectModelI = {
            showResult: true,
            buttonText: "Share",
            extractItems: (items: any) => {
                return items.searchCriteria.item.map((item: any) => ({
                    itemId: item.itemName,
                    itemName: item.itemName,
                }));
            },
            addAction: (item: S25ItemI) => {
                BoardService.shareBoard(board.boardId, board.boardUUID, item.itemId as string).catch(S25Util.showError);
            },
            removeAction: (item: S25ItemI) => {
                BoardService.unshareBoard(board.boardId, board.boardUUID, item.itemId as string).catch(
                    S25Util.showError,
                );
            },
        };

        let editors: Table.Cell = {
            inputs: {
                type: "contacts",
                customFilterValue: "&is_r25user=1&itemName=username",
                selectedItems: board.sharedUsers.map((user) => ({
                    itemId: user.username,
                    itemName: user.username,
                })),
                modelBean: multiselectModel,
                popoverOnBody: true,
            },
        };
        if (board.shared) {
            const users = [
                ...board.sharedUsers
                    .filter((item) => item.username !== this.currentUsername)
                    .map((item) => ({ text: item.username })),
            ];
            editors = {
                component: GenericTableListComponent,
                inputs: {
                    items: users,
                },
            };
        }

        const pushbackItems = [{ text: "Location Assignments" }];
        if (board.mpgPushback) pushbackItems.push({ text: "Meeting Patterns" });

        return {
            id: board.boardId,
            name: board.boardName,
            data: { board },
            cells: {
                board: { text: board.boardName },
                locationSearch: { text: board.locationQueryName },
                eventSearch: { text: board.eventQueryName },
                lastRun: {
                    text: S25Datefilter.transform(board.lastPrepared, this.dateFormat),
                    sortValue: board.lastPrepared,
                },
                editors,
                pushback: {
                    component: GenericTableListComponent,
                    inputs: { items: pushbackItems },
                },
                edit: {
                    if: board.isOwner,
                },
            },
        };
    }

    forceRefresh() {
        return this.table?.refresh(true);
    }

    @Bind
    async onEditClick(row: Table.Row) {
        const board: SwarmSchedule.BoardI = row.data.board;
        await this.editModal.open({
            title: `Edit Grid: ${board.boardName}`,
            templateData: { board },
        });
        this.table.updateRow(this.mapToRow(board));
    }
}
